import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { toast } from "@redotech/redo-web/alert";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { memo, useEffect, useRef, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

interface RecognizeMercanaVipBackfillProps {
  teamId: string;
  enabled: boolean;
}

export const RecognizeMercanaVipBackfill = memo(
  function RecognizeMercanaVipBackfill({
    teamId,
    enabled,
  }: RecognizeMercanaVipBackfillProps) {
    const client = useRequiredContext(RedoAdminRpcClientContext);
    const status = useRpcQuery(RedoAdminRpcClientContext, {
      method: "getRecognizeMercanaVipBackfillStatus",
      args: { teamId },
      options: {
        enabled,
        staleTime: 0,
        refetchInterval: (query) => {
          if (query.state.data?.status === "RUNNING") {
            return 5000;
          }
          return false;
        },
      },
    });
    const previousStatus = useRef<{ teamId: string; status: string } | null>(
      null,
    );
    const completionToast = useRef<ReturnType<typeof toast> | null>(null);

    useEffect(() => {
      return () => {
        previousStatus.current = null;
        completionToast.current?.close();
        completionToast.current = null;
      };
    }, [teamId]);

    useEffect(() => {
      const data = status.data;
      const hasFreshStatus = status.isFetchedAfterMount && data != null;
      if (!hasFreshStatus) {
        return;
      }

      const completedWhileViewing =
        previousStatus.current?.teamId === teamId &&
        previousStatus.current.status === "RUNNING" &&
        data.status === "COMPLETED";
      previousStatus.current = { teamId, status: data.status };

      if (completedWhileViewing && data.progress) {
        completionToast.current = toast(
          `VIP backfill complete · ${data.progress.updated.toLocaleString()} profiles updated`,
          { variant: "success" },
        );
      }
    }, [status.data, status.isFetchedAfterMount, teamId]);

    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [run, start] = useTriggerLoad(async () => {
      setConfirmModalOpen(false);

      const result = await client.backfillRecognizeMercanaVips({ teamId });
      await status.refetch();
      if (!result.started) {
        toast(result.message, { variant: "error" });
        return;
      }
      toast("Mercana VIP backfill started in the background.", {
        variant: "success",
      });
    });
    const isRunning = status.data?.status === "RUNNING";
    const disabled =
      !enabled ||
      status.isPending ||
      status.isError ||
      run.pending ||
      isRunning;
    const progress = status.data?.progress;
    const hasFailed =
      status.data != null && !isRunning && status.data.status !== "COMPLETED";

    return (
      <Flex dir="column" gap="md">
        <Text fontSize="sm" textColor="secondary">
          Recalculate VIP categories from saved successful Mercana responses and
          refresh audience insights. No new research or automations run.
        </Text>
        <RedoButton
          disabled={disabled}
          hierarchy="secondary"
          onClick={() => setConfirmModalOpen(true)}
          size="sm"
          text="Backfill Mercana VIPs"
        />
        <RedoModal
          isOpen={confirmModalOpen}
          modalSize="sm"
          onModalCloseRequested={() => setConfirmModalOpen(false)}
          primaryButton={{
            text: "Backfill VIP categories",
            onClick: () => void start(),
            disabled,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setConfirmModalOpen(false),
          }}
          theme="warn"
          title="Backfill Mercana VIP categories?"
        >
          <Flex dir="column" gap="md" p="lg">
            <Text fontSize="sm" textColor="secondary">
              Recalculate VIP categories for this store’s completed Mercana
              profiles using their saved responses. This makes no new Mercana
              requests and does not run automations.
            </Text>
          </Flex>
        </RedoModal>
        {isRunning && (
          <Text fontSize="sm" textColor="secondary">
            RUNNING
            {progress &&
              ` · ${progress.processed.toLocaleString()} processed · ${progress.updated.toLocaleString()} updated · ${progress.skipped.toLocaleString()} skipped`}
          </Text>
        )}
        {hasFailed && (
          <Text fontSize="sm" textColor="error">
            VIP backfill did not complete: {status.data?.status}
          </Text>
        )}
        {status.error && (
          <Text fontSize="sm" textColor="error">
            Unable to load backfill status: {status.error.message}
          </Text>
        )}
        {run.error && (
          <Text fontSize="sm" textColor="error">
            Unable to start backfill: {run.error.message}
          </Text>
        )}
      </Flex>
    );
  },
);
